/* eslint-disable no-unused-vars */
import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';

import {
  Table,
  IconButton as IconButtonWithText,
  DeleteConfirmationModal,
  Loader,
  DropdownButton,
} from '../../../Components/uielements';
import { useUserContext } from '../../../hooks/UserContext';
import { useCompany } from '../../../hooks/CompanyContext';
import PremiseService, {
  getAllPremiseAsDistributorExcel,
} from '../../../services/bioplus-services/premise';
import { CompanyContainer } from './premise.style';
import moment from 'moment';
import { AddPlusIcon, DeleteIcon, EditIcon } from '../../../Components/icons';
import { AddPremiseModal, EditPremiseModal } from './modals';
import { IntlMessages } from '../../../helper/langHelper';
import { exportExcelFile } from '../../../helper/exportExcelHelper';

export function extractData(dataArray) {
  if (dataArray) {
    const extractedData = dataArray?.map((item) => {
      return {
        premiseName: item?.propertyName,
        propertyId: item?.propertyId ?? '--',
        address: `${item?.addressStreet}, ${item?.addressTown}, ${item?.addressState}` ?? '--',
        contactNumber: item?.contactNumber ?? '--',
        // premiseRange: item?.premiseRange ?? '--',
        contactEmail: item?.contactEmail ?? '--',
        createdAt: moment(item?.createdAt).format('DD-MM-YYYY'),
      };
    });
    return { bioplusUsers: extractedData };
  }
}

const PartnerPremises = () => {
  const userContext = useUserContext();
  const companyContext = useCompany();
  const companyFilter = companyContext?.company?._id;
  // const partnerId = userContext.company.companyId?.id;
  const partnerId = userContext.company?.partnerId;
  const userRole = userContext.company;
  const roleId = userContext?.company?._id;

  const { userPermission } = userContext;
  const isUserAllowToManage = userPermission?.find((permission) => permission?.label === 'premise');
  const canDownloadReport = !!isUserAllowToManage?.isReport;
  const canManagePremise = !!isUserAllowToManage?.isManage;
  const canCreatePremise = !!isUserAllowToManage?.isCreate;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [name, setName] = useState();
  const [nameFilter, setNameFilter] = useState();

  const [editPremise, setEditPremise] = useState(undefined);
  const [deletePremise, setDeletePremise] = useState();
  const [showAddPremise, setShowAddPremise] = useState(false);

  const queryClient = useQueryClient();

  const usersQuery = useQuery(
    ['premises', partnerId, companyFilter, nameFilter, page, pageSize],
    () =>
      PremiseService.listByPartner(
        partnerId,
        { companyId: companyFilter, name: nameFilter },
        page,
        pageSize
      )
  );

  const getAllPremises = useQuery(['allPremisesForReport', partnerId, companyFilter], () =>
    getAllPremiseAsDistributorExcel(partnerId, companyFilter)
  );

  const deleteMutation = useMutation((premise) => PremiseService.remove(premise), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('premises');
      setShowAddPremise(undefined);
    },
    onError: (error) => {
      alert(error?.response?.data?.message);
      setShowAddPremise(undefined);
    },
  });

  const userTableData = usersQuery?.data?.data ?? [];

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const handleClosedEdit = () => {
    setEditPremise(undefined);
  };
  const handleClosedDeletePremise = () => {
    setDeletePremise(undefined);
  };
  const handleDeletePremise = (premise) => {
    deleteMutation.mutate(premise);
    setDeletePremise(undefined);
  };

  // Edit/Delete actions require manage permission AND a selected company
  const canShowActions = canManagePremise && !!companyFilter;

  const actionList = [
    ...(canShowActions
      ? [
          (data) => (
            <IconButton onClick={() => setEditPremise(data)}>
              <EditIcon style={{ width: '18px', height: '18px' }} />
            </IconButton>
          ),
          (data) => (
            <IconButton onClick={() => setDeletePremise(data)}>
              <DeleteIcon style={{ width: '19px', height: '19px' }} />
            </IconButton>
          ),
        ]
      : []),
  ];

  //   table coulmns
  const userColumns = useMemo(() => {
    const cols = [
      {
        Header: <IntlMessages id="table.col.premise.name" />,
        accessor: 'propertyName',
        width: canShowActions ? '15%' : '20%',
      },
      {
        Header: <IntlMessages id="table.col.property.id" />,
        accessor: 'propertyId',
        width: '10%',
        Cell: (cellData) => (
          <TableCell>{`${
            cellData?.row?.original?.propertyId ? cellData?.row?.original?.propertyId : '--'
          }`}</TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.premise.address" />,
        accessor: 'addressState',
        width: '25%',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.addressStreet}, ${cellData?.row?.original?.addressTown}, ${cellData?.row?.original?.addressState}`}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.premise.person.contact" />,
        accessor: 'contactNumber',
        width: '15%',
        Cell: (cellData) => <TableCell>{cellData.value}</TableCell>,
      },
      // {
      //   Header: <IntlMessages id="features.premise.range" />,
      //   accessor: 'premiseRange',
      //   width: '10%',
      //   Cell: (cellData) => <TableCell>{cellData.value}</TableCell>,
      // },
      {
        Header: <IntlMessages id="table.col.creator" />,
        accessor: 'contactEmail',
        width: '18%',
        Cell: (cellData) => <TableCell>{cellData.value}</TableCell>,
      },
      {
        Header: <IntlMessages id="table.col.createdAt" />,
        accessor: 'createdAt',
        width: '10%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
    ];

    if (canShowActions) {
      cols.push({
        Header: <IntlMessages id="table.col.actions" />,
        accessor: 'actions',
        isAction: true,
        width: '20%',
      });
    }

    return cols;
  }, [canShowActions]);

  const excelFormatData = extractData(getAllPremises?.data || []);
  const fileName = `Properties-${userRole?.companyId?.name}-${moment().format(
    'DD-MM-YYYY-hh.mm A'
  )}.xlsx`;
  const fileNameCsv = `Properties-${userRole?.companyId?.name}-${moment().format(
    'DD-MM-YYYY-hh.mm A'
  )}.csv`;

  const columnConfig = [
    { header: 'Property Name', key: 'premiseName', width: 30 },
    { header: 'Property ID', key: 'propertyId', width: 30 },
    { header: 'Address', key: 'address', width: 30 },
    { header: 'Contact Number', key: 'contactNumber', width: 30 },
    // { header: 'Premise Range', key: 'premiseRange', width: 30 },
    { header: 'Contact Email', key: 'contactEmail', width: 30 },
    { header: 'Created Date', key: 'createdAt', width: 30 },
  ];

  if (usersQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  const handleClickItem = (item) => {
    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  // Add Premise button requires create permission AND a selected company
  const canShowAddPremise = canCreatePremise && !!companyFilter;

  return (
    <StyledContainer>
      <CompanyContainer>
        <HeaderTitle>
          <IntlMessages id="nav.premises" />
        </HeaderTitle>
        {/* {excelFormatData && (
          <IconButtonWithText
            text={<IntlMessages id="button.text.downloadExcel" />}
            icon={<DownloadIcon />}
            onClick={() => exportExcelFile(excelFormatData, fileName, columnConfig)}
          />
        )} */}
        <TwoColumns style={{ gap: '10px' }}>
          {excelFormatData && canDownloadReport && (
            <DropdownButton
              noOfColoumns={1}
              buttonArray={[
                { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
                // { key: 'pdf', title: 'Export as PDF', icon: <PictureAsPdfIcon /> },
                { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
              ]}
              onClick={handleClickItem}
            />
          )}
          {canShowAddPremise && (
            <IconButtonWithText
              text={<IntlMessages id="button.text.addPremise" />}
              icon={<AddPlusIcon />}
              onClick={() => {
                setShowAddPremise(true);
              }}
            />
          )}
        </TwoColumns>
      </CompanyContainer>
      <TableWrapper>
        <Table
          data={userTableData}
          columns={userColumns}
          hasFooter={true}
          actions={actionList}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={usersQuery?.data?.total || 0}
        />
      </TableWrapper>
      {showAddPremise && canShowAddPremise && (
        <AddPremiseModal
          onClose={() => setShowAddPremise(false)}
          companyId={companyFilter}
          roleId={roleId}
          invalidateQuery={'premises'}
        />
      )}
      {editPremise && editPremise !== undefined && canShowActions && (
        <EditPremiseModal
          premise={editPremise}
          onClose={handleClosedEdit}
          userRole="owner"
          invalidateQuery={'premises'}
        />
      )}
      {deletePremise && canShowActions && (
        <DeleteConfirmationModal
          onClose={handleClosedDeletePremise}
          message={
            <IntlMessages
              id="delete.comfirmation.text"
              values={{ premise: deletePremise.propertyName }}
            />
          }
          onConfirm={() => handleDeletePremise(deletePremise)}
        />
      )}
    </StyledContainer>
  );
};

export default PartnerPremises;
